import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Eye, EyeOff, Loader2, Mail } from "lucide-react";

import LinkedAuthLanguageStep from "@/components/onboarding/LinkedAuthLanguageStep";
import Logo from "@/components/shared/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  buildLinkedAccountLanguagePayload,
  buildSecondaryLanguageOptions,
  persistLinkedAccountLanguages,
  shouldBypassSuccessAuthForAuthenticatedUser,
  shouldApplySuggestedLinkedHomeLanguage,
} from "@/lib/account-linking";
import { useLinkedAuthCallbackRecovery } from "@/components/onboarding/useLinkedAuthCallbackRecovery";
import {
  adoptOnboardingAccount,
  captureOnboardingEmail,
  createOnboardingHandoff,
  finalizeOnboardingSignup,
  getCurrentAccountId,
  getMyAccount,
  getPreferencesBootstrap,
  getOnboardingEmailStatus,
  getRuntimeConfig,
  openSupportEmailClaim,
  saveAccountLanguages,
  setCurrentAccountId,
} from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import {
  buildLanguageSelectOptions,
  normalizeLanguageCode,
  normalizeUiLanguageCode,
} from "@/lib/languages";
import {
  buildInitialOnboardingAuthState,
  buildRetryOnboardingAuthState,
  clearOnboardingAuthDraft,
  clearOnboardingHandoffToken,
  getOnboardingAuthFieldProps,
  getOnboardingAuthUiState,
  getOnboardingEmailCodeVerifyTypes,
  getPreferredSupabaseUser,
  isAuthenticatedSupabaseUser,
  getOnboardingEmailRedirectUrl,
  isMatchingSupabaseEmailUser,
  isVerifiedSupabaseEmailUser,
  normalizeOnboardingEmailCode,
  normalizeOnboardingEmailAddress,
  normalizeOnboardingAuthState,
  readOnboardingAuthDraft, readOnboardingAuthDraftUserMode,
  readOnboardingHandoffTokenFromSearch,
  retryOnboardingAuthOperation,
  resolveOnboardingAuthDraftContextKey,
  resolveOnboardingEmailUserMode,
  shouldUpdateVerifiedSignupPassword,
  storeOnboardingAuthDraft,
  storeOnboardingHandoffToken,
} from "@/lib/onboarding-auth";
import {
  readSupportEmailClaimCallback,
  readSupportEmailClaimToken,
  readScrubbedOnboardingHandoffToken,
  readStoredLinkedAuthHandoffToken,
  resolvePlainLinkedAuthHandoffToken,
} from "@/lib/onboarding-auth-handoff";
import {
  DESIGN_USER_VALUES,
  getOnboardingDesignState,
  readOnboardingDesignParams,
  updateOnboardingDesignState,
} from "@/lib/onboarding-design-mode";
import { getShareLinkEntry } from "@/lib/share-link-entry";
import { getSupabaseBrowserClient } from "@/lib/supabase-browser-client";
import { readSupabaseAuthCallbackFromUrl, syncSupabaseSessionFromUrl } from "@/lib/supabase-session";

async function getCurrentSupabaseUser(client) {
  const {
    data: { session },
    error: sessionError,
  } = await client.auth.getSession();
  if (sessionError) {
    throw sessionError;
  }
  const {
    data: userPayload,
    error: userError,
  } = await client.auth.getUser();
  if (userError && session?.user) {
    return session.user;
  }
  if (userError) {
    throw userError;
  }
  return getPreferredSupabaseUser({
    fetchedUser: userPayload.user,
    sessionUser: session?.user || null,
  });
}

export default function LinkedSuccessAuth({
  search = "",
  onSkip,
  onAuthenticated,
  surface = "success",
  lockUserMode = false, emailRedirectPath = "/auth",
}) {
  const { locale, setLocale, t, tx } = useI18n();
  const storedState = useMemo(() => getOnboardingDesignState(), []);
  const params = useMemo(() => readOnboardingDesignParams(search), [search]);
  const shareLinkEntry = useMemo(() => getShareLinkEntry(), []);
  const isSignInSurface = surface === "signin";
  const supportEmailClaimToken = readSupportEmailClaimToken(search);
  const hasSupportEmailClaim = !isSignInSurface && Boolean(supportEmailClaimToken);
  const callbackHandoffToken = readOnboardingHandoffTokenFromSearch(search) || readScrubbedOnboardingHandoffToken() || readStoredLinkedAuthHandoffToken(search) || "";
  const hasSupportEmailClaimCallback = !isSignInSurface
    && Boolean(callbackHandoffToken)
    && readSupportEmailClaimCallback(search);
  const linkedAuthHandoffToken = resolvePlainLinkedAuthHandoffToken({
    handoffToken: callbackHandoffToken,
    hasSupportEmailClaim,
    hasSupportEmailClaimCallback,
    isSignInSurface,
  });
  const [supportEmailClaim, setSupportEmailClaim] = useState({
    status: hasSupportEmailClaim ? "loading" : "idle",
    handoffToken: "",
    error: "",
  });
  const supportEmailClaimLoading = supportEmailClaim.status === "loading";
  const supportEmailClaimHandoffToken = String(
    supportEmailClaim.handoffToken || (hasSupportEmailClaimCallback ? callbackHandoffToken : ""),
  ).trim();
  const authDraftContextKey = useMemo(
    () => resolveOnboardingAuthDraftContextKey({
      isSignInSurface,
      supportEmailClaimToken: hasSupportEmailClaim ? supportEmailClaimToken : "",
      supportEmailClaimHandoffToken,
      supportEmailClaimCallback: hasSupportEmailClaimCallback,
      linkedHandoffToken: linkedAuthHandoffToken,
      currentAccountId: getCurrentAccountId(),
    }),
    [hasSupportEmailClaim, hasSupportEmailClaimCallback, isSignInSurface, linkedAuthHandoffToken, supportEmailClaimHandoffToken, supportEmailClaimToken],
  );
  const storedAuthDraft = useMemo(() => readOnboardingAuthDraft(authDraftContextKey), [authDraftContextKey]);
  const normalizedStoredAuth = buildInitialOnboardingAuthState({
    storedAuth: storedState.auth,
    storedAuthDraft,
    isolateStoredAuth: hasSupportEmailClaim || hasSupportEmailClaimCallback || Boolean(linkedAuthHandoffToken),
  });
  const shouldReuseStoredDraft = normalizedStoredAuth.status === "email-sent"
    && (!lockUserMode || normalizedStoredAuth.userMode === "existing");
  const initialUserMode = lockUserMode
    ? "existing"
    : shouldReuseStoredDraft
    ? normalizedStoredAuth.userMode
    : DESIGN_USER_VALUES.includes(params.demoUser)
      ? params.demoUser
      : isSignInSurface ? "" : "new";

  const [userMode, setUserMode] = useState(initialUserMode);
  const [status, setStatus] = useState(shouldReuseStoredDraft ? "email-sent" : "idle");
  const [email, setEmail] = useState(shouldReuseStoredDraft ? normalizedStoredAuth.email || "" : "");
  const [firstName, setFirstName] = useState(shouldReuseStoredDraft ? normalizedStoredAuth.firstName || "" : "");
  const [lastName, setLastName] = useState(shouldReuseStoredDraft ? normalizedStoredAuth.lastName || "" : "");
  const [password, setPassword] = useState(shouldReuseStoredDraft ? normalizedStoredAuth.passwordDraft || "" : "");
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState("");
  const [verificationError, setVerificationError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [emailCode, setEmailCode] = useState("");
  const [runtimeConfig, setRuntimeConfig] = useState(null);
  const emailInputRef = useRef(null);
  const emailCheckRef = useRef(0);
  const authCallbackErrorHandledRef = useRef(false);
  const autoResumeAttemptedRef = useRef(false);
  const passwordSubmittedDuringSignupRef = useRef(shouldReuseStoredDraft && normalizedStoredAuth.passwordSubmittedDuringSignup === true);
  const successBypassAttemptedRef = useRef(false);
  const languageTouchedRef = useRef(false);
  const emailStatusFallbackUserMode = isSignInSurface ? "existing" : "new";
  const browserLanguages = typeof navigator !== "undefined" && Array.isArray(navigator.languages)
    ? navigator.languages
    : (typeof navigator !== "undefined" && navigator.language ? [navigator.language] : []);
  const suggestedLanguageCode = normalizeLanguageCode(
    shareLinkEntry?.uiLanguageHintCode
      || shareLinkEntry?.deliveryLanguageCode
      || runtimeConfig?.countryLanguageSuggestions?.[0]
      || browserLanguages[0]
      || locale,
  );
  const [homeLanguageCode, setHomeLanguageCode] = useState(suggestedLanguageCode);
  const [understoodLanguageCodes, setUnderstoodLanguageCodes] = useState(() => {
    const seed = [];
    const englishCode = "en";
    const shareLinkLanguageCode = normalizeLanguageCode(shareLinkEntry?.deliveryLanguageCode || "", "");
    if (shareLinkLanguageCode && shareLinkLanguageCode !== suggestedLanguageCode) {
      seed.push(shareLinkLanguageCode);
    }
    if (suggestedLanguageCode !== englishCode) {
      seed.push(englishCode);
    }
    return [...new Set(seed)];
  });
  const [languageStepComplete, setLanguageStepComplete] = useState(
    isSignInSurface || normalizedStoredAuth.languageStepComplete,
  );
  const languageOptions = useMemo(() => buildLanguageSelectOptions({
    countryCode: runtimeConfig?.countryCode || "",
    browserLanguages,
    shareLinkLanguageCode: shareLinkEntry?.uiLanguageHintCode || shareLinkEntry?.deliveryLanguageCode || "",
    preferredUiLanguageCode: homeLanguageCode || suggestedLanguageCode,
    uiOnly: false,
  }), [browserLanguages, homeLanguageCode, runtimeConfig?.countryCode, shareLinkEntry?.deliveryLanguageCode, shareLinkEntry?.uiLanguageHintCode, suggestedLanguageCode]);
  const otherLanguageOptions = useMemo(
    () => buildSecondaryLanguageOptions(languageOptions, { homeLanguageCode }),
    [homeLanguageCode, languageOptions],
  );
  const languagePayload = useMemo(
    () => buildLinkedAccountLanguagePayload({
      homeLanguageCode,
      locale,
      understoodLanguageCodes,
    }),
    [homeLanguageCode, locale, understoodLanguageCodes],
  );

  const {
    emailValid,
    namesComplete,
    showNameFields,
    showPasswordField,
    canSubmit,
    primaryLabel,
    resolvedUserMode,
  } = getOnboardingAuthUiState({
    email,
    firstName,
    lastName,
    passwordDraft: password,
    userMode,
    checkingEmail,
    forceShowPasswordField: isSignInSurface,
  });
  const fieldProps = getOnboardingAuthFieldProps(resolvedUserMode || userMode, { unresolvedUserMode: isSignInSurface ? "existing" : "new" });
  const runRetryableSupabaseAuthOperation = useCallback(
    (operation) => retryOnboardingAuthOperation(async () => {
      const result = await operation();
      if (result?.error) {
        throw result.error;
      }
      return result;
    }),
    [],
  );
  const isBusy = submitting || verifying || supportEmailClaimLoading;
  const submitPendingLabel = resolvedUserMode === "existing" && password.trim()
    ? "Signing in…"
    : resolvedUserMode === "new" && isSignInSurface && password.trim()
    ? "Creating account…"
    : "Sending email…";
  const signInEmailRedirectPath = String(emailRedirectPath || "").trim() || "/auth";
  const linkedEmailRedirectPath = supportEmailClaimHandoffToken
    ? "/auth?context=linked&supportClaim=1"
    : (
      !isSignInSurface
      && String(emailRedirectPath || "").includes("context=linked")
        ? emailRedirectPath
        : "/auth?context=linked"
    );

  useEffect(() => {
    if (!hasSupportEmailClaim) {
      setSupportEmailClaim({ status: "idle", handoffToken: "", error: "" });
      return undefined;
    }

    let cancelled = false;
    setSupportEmailClaim({ status: "loading", handoffToken: "", error: "" });

    async function openClaim() {
      try {
        const payload = await openSupportEmailClaim(supportEmailClaimToken);
        const handoffToken = String(payload?.token || "").trim();
        if (!handoffToken) {
          throw new Error("This email link could not be opened. Message Min Support for a new one.");
        }
        storeOnboardingHandoffToken(handoffToken);
        if (!cancelled) {
          setSupportEmailClaim({ status: "ready", handoffToken, error: "" });
        }
      } catch (error) {
        if (!cancelled) {
          setSupportEmailClaim({
            status: "error",
            handoffToken: "",
            error: error instanceof Error
              ? error.message
              : "This email link could not be opened. Message Min Support for a new one.",
          });
        }
      }
    }

    void openClaim();

    return () => {
      cancelled = true;
    };
  }, [hasSupportEmailClaim, supportEmailClaimToken]);

  useEffect(() => {
    if (
      successBypassAttemptedRef.current
      || isSignInSurface
      || hasSupportEmailClaim
      || hasSupportEmailClaimCallback
      || linkedAuthHandoffToken
      || status !== "idle"
      || !languageStepComplete
    ) {
      return undefined;
    }

    const client = getSupabaseBrowserClient();
    if (!client) {
      return undefined;
    }

    let cancelled = false;

    async function maybeBypassSuccessAuth() {
      successBypassAttemptedRef.current = true;
      try {
        await syncSupabaseSessionFromUrl({ client });
        const user = await getCurrentSupabaseUser(client);
        if (!isAuthenticatedSupabaseUser(user)) {
          return;
        }
        const accountPayload = await getPreferencesBootstrap();
        if (!shouldBypassSuccessAuthForAuthenticatedUser({
          isSignInSurface,
          authStatus: status,
          storedUserMode: readOnboardingAuthDraftUserMode(authDraftContextKey) || (!(hasSupportEmailClaim || hasSupportEmailClaimCallback || Boolean(linkedAuthHandoffToken)) && DESIGN_USER_VALUES.includes(String(storedState.auth?.userMode || "").trim().toLowerCase()) ? String(storedState.auth.userMode || "").trim().toLowerCase() : ""),
          handoffToken: linkedAuthHandoffToken,
          languageStepComplete,
          accountPayload,
        })) {
          return;
        }
        const handoffToken = linkedAuthHandoffToken;
        const resolvedAccountId = String(
          getCurrentAccountId()
          || accountPayload?.account?.id
          || "",
        ).trim();
        const linkedAccountId = await persistLinkedAccountLanguages({
          currentAccountId: resolvedAccountId,
          handoffToken,
          languagePayload,
          adoptOnboardingAccount,
          saveAccountLanguages,
          setCurrentAccountId,
          clearOnboardingHandoffToken,
        });
        clearOnboardingAuthDraft(authDraftContextKey);
        if (!cancelled) {
          onAuthenticated?.({
            userMode: "existing",
            accountId: linkedAccountId,
            setupMode: "existing",
          });
        }
      } catch {
        // fall back to the visible auth flow when the current browser session cannot prove ownership
      }
    }

    void maybeBypassSuccessAuth();

    return () => {
      cancelled = true;
    };
  }, [
    authDraftContextKey,
    hasSupportEmailClaim,
    hasSupportEmailClaimCallback,
    isSignInSurface,
    languagePayload,
    languageStepComplete,
    linkedAuthHandoffToken,
    locale,
    normalizedStoredAuth.userMode,
    onAuthenticated,
    status,
  ]);

  useEffect(() => {
    let cancelled = false;
    if (isSignInSurface) {
      return undefined;
    }
    async function loadRuntimeConfig() {
      try {
        const payload = await getRuntimeConfig();
        if (!cancelled) {
          setRuntimeConfig(payload);
        }
      } catch {
        if (!cancelled) {
          setRuntimeConfig(null);
        }
      }
    }
    void loadRuntimeConfig();
    return () => {
      cancelled = true;
    };
  }, [isSignInSurface]);

  useEffect(() => {
    if (!shouldApplySuggestedLinkedHomeLanguage({
      isSignInSurface,
      languageTouched: languageTouchedRef.current,
      currentLanguageCode: homeLanguageCode,
    })) {
      return;
    }
    setHomeLanguageCode((current) => (
      shouldApplySuggestedLinkedHomeLanguage({
        isSignInSurface,
        languageTouched: languageTouchedRef.current,
        currentLanguageCode: current,
      })
        ? suggestedLanguageCode
        : current
    ));
  }, [homeLanguageCode, isSignInSurface, suggestedLanguageCode]);

  useEffect(() => {
    if (status === "idle" && emailInputRef.current) {
      emailInputRef.current.focus();
      emailInputRef.current.select();
    }
  }, [status]);

  useEffect(() => {
    if (status !== "idle") {
      return undefined;
    }
    if (lockUserMode) {
      setCheckingEmail(false);
      setUserMode("existing");
      return undefined;
    }
    if (!emailValid) {
      emailCheckRef.current += 1;
      setCheckingEmail(false);
      setUserMode("");
      return undefined;
    }

    const requestId = emailCheckRef.current + 1;
    emailCheckRef.current = requestId;
    setCheckingEmail(true);
    setUserMode("");

    const timeoutId = window.setTimeout(() => {
      const normalizedEmail = normalizeOnboardingEmailAddress(email);
      void resolveOnboardingEmailUserMode(normalizedEmail, {
        loadEmailStatus: getOnboardingEmailStatus,
        fallbackUserMode: emailStatusFallbackUserMode,
        timeoutMs: 4_000,
        retries: 1,
      })
        .then((payload) => {
          if (emailCheckRef.current !== requestId) {
            return;
          }
          setUserMode(payload);
        })
        .finally(() => {
          if (emailCheckRef.current === requestId) {
            setCheckingEmail(false);
          }
        });
    }, 350);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [email, emailStatusFallbackUserMode, emailValid, lockUserMode, status]);

  const persistAuth = useCallback((nextStatus, overrides = {}) => {
    const nextAuth = normalizeOnboardingAuthState({
      email,
      firstName,
      lastName,
      passwordDraft: password,
      passwordSubmittedDuringSignup: passwordSubmittedDuringSignupRef.current,
      userMode,
      languageStepComplete,
      status: nextStatus,
      ...overrides,
    });
    if (!hasSupportEmailClaim) {
      updateOnboardingDesignState((current) => ({
        ...current,
        auth: nextAuth,
      }));
    }
    storeOnboardingAuthDraft(nextAuth, authDraftContextKey);
  }, [authDraftContextKey, email, firstName, hasSupportEmailClaim, languageStepComplete, lastName, password, userMode]);

  const handleLanguageStepComplete = async () => {
    if (
      !isSignInSurface
      && !hasSupportEmailClaim
      && !hasSupportEmailClaimCallback
      && !linkedAuthHandoffToken
    ) {
      setSubmitting(true);
      setFormError("");
      try {
        const linkedAccountId = await persistLinkedAccountLanguages({
          currentAccountId: String(getCurrentAccountId() || "").trim(),
          languagePayload,
          adoptOnboardingAccount,
          saveAccountLanguages,
          setCurrentAccountId,
          clearOnboardingHandoffToken,
        });
        persistAuth("verified", { userMode: "new", languageStepComplete: true });
        clearOnboardingAuthDraft(authDraftContextKey);
        onAuthenticated?.({
          userMode: "new",
          accountId: linkedAccountId,
          setupMode: "new",
        });
        return;
      } catch (error) {
        setFormError(
          error instanceof Error
            ? error.message
            : "We could not save your language choice. Try again.",
        );
      } finally {
        setSubmitting(false);
      }
      return;
    }
    setLanguageStepComplete(true);
    persistAuth(status, { languageStepComplete: true });
  };

  const handleUseAnotherEmail = () => {
    if (isBusy) {
      return;
    }
    const retryState = buildRetryOnboardingAuthState({
      status,
      email,
      firstName,
      lastName,
      passwordDraft: password,
      userMode,
    });

    setStatus(retryState.status);
    setEmail(retryState.email);
    setFirstName(retryState.firstName);
    setLastName(retryState.lastName);
    setPassword(retryState.passwordDraft);
    passwordSubmittedDuringSignupRef.current = retryState.passwordSubmittedDuringSignup;
    setShowPassword(false);
    setCheckingEmail(false);
    setEmailCode("");
    setUserMode(lockUserMode ? "existing" : "");
    setFormError("");
    setVerificationError("");
    persistAuth(retryState.status, retryState);
    requestAnimationFrame(() => {
      if (emailInputRef.current) {
        emailInputRef.current.focus();
        emailInputRef.current.select();
      }
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit || isBusy) {
      return;
    }

    const client = getSupabaseBrowserClient();
    if (!client) {
      setFormError("Supabase is not configured.");
      return;
    }

    const normalizedEmail = normalizeOnboardingEmailAddress(email);
    if (normalizedEmail !== email) {
      setEmail(normalizedEmail);
    }

    setSubmitting(true);
    setFormError("");
    setVerificationError("");

    try {
      let submitUserMode = lockUserMode ? "existing" : resolvedUserMode;
      if (!submitUserMode && emailValid) {
        submitUserMode = await resolveOnboardingEmailUserMode(normalizedEmail, {
          loadEmailStatus: getOnboardingEmailStatus,
          fallbackUserMode: emailStatusFallbackUserMode,
          timeoutMs: 4_000,
          retries: 1,
        });
      }
      if (!submitUserMode) {
        submitUserMode = lockUserMode ? "existing" : emailStatusFallbackUserMode;
      }
      setUserMode(submitUserMode);

      if (submitUserMode === "new" && !namesComplete) {
        return;
      }

      if (submitUserMode === "new") {
        if (isSignInSurface) {
          if (password.trim()) {
            await runRetryableSupabaseAuthOperation(() => client.auth.signUp({
              email: normalizedEmail,
              password,
              options: {
                data: {
                  first_name: firstName,
                  last_name: lastName,
                },
                emailRedirectTo: getOnboardingEmailRedirectUrl(emailRedirectPath),
              },
            }));
          } else {
            await runRetryableSupabaseAuthOperation(() => client.auth.signInWithOtp({
              email: normalizedEmail,
              options: {
                shouldCreateUser: true,
                emailRedirectTo: getOnboardingEmailRedirectUrl(emailRedirectPath),
              },
            }));
          }

          persistAuth("email-sent", { userMode: submitUserMode });
          setStatus("email-sent");
          setEmailCode("");
          return;
        }

        const directHandoffToken = String(supportEmailClaimHandoffToken || linkedAuthHandoffToken || "").trim();
        if (directHandoffToken) {
          storeOnboardingHandoffToken(directHandoffToken);
          const emailRedirectTo = getOnboardingEmailRedirectUrl(linkedEmailRedirectPath, { handoffToken: directHandoffToken });
          await captureOnboardingEmail({
            email: normalizedEmail,
            authMethod: password.trim() ? "password" : "magic_link",
            userMode: submitUserMode,
            surface,
          }).catch(() => null);
          if (password.trim()) {
            await runRetryableSupabaseAuthOperation(() => client.auth.signUp({
              email: normalizedEmail,
              password,
              options: {
                data: {
                  first_name: firstName,
                  last_name: lastName,
                },
                emailRedirectTo,
              },
            }));
          } else {
            await runRetryableSupabaseAuthOperation(() => client.auth.signInWithOtp({
              email: normalizedEmail,
              options: {
                shouldCreateUser: true,
                emailRedirectTo,
              },
            }));
          }
          passwordSubmittedDuringSignupRef.current = Boolean(password.trim());

          persistAuth("email-sent", { userMode: submitUserMode, passwordSubmittedDuringSignup: passwordSubmittedDuringSignupRef.current });
          setStatus("email-sent");
          setEmailCode("");
          return;
        }

        const linkedAccountId = String(getCurrentAccountId() || "").trim();
        if (!linkedAccountId) {
          throw new Error("No linked account is available yet.");
        }

        const handoff = await createOnboardingHandoff(linkedAccountId);
        storeOnboardingHandoffToken(handoff.token);

        await runRetryableSupabaseAuthOperation(() => client.auth.updateUser({
          email: normalizedEmail,
          data: {
            first_name: firstName,
            last_name: lastName,
          },
        }, {
          emailRedirectTo: getOnboardingEmailRedirectUrl(linkedEmailRedirectPath, {
            handoffToken: handoff.token,
          }),
        }));

        await captureOnboardingEmail({
          email: normalizedEmail,
          authMethod: password.trim() ? "password" : "magic_link",
          userMode: submitUserMode,
          surface,
        }).catch(() => null);
        persistAuth("email-sent", { userMode: submitUserMode });
        setStatus("email-sent");
        setEmailCode("");
        return;
      }

      const accountId = getCurrentAccountId();
      if (
        !accountId
        && !isSignInSurface
        && !supportEmailClaimHandoffToken
        && !linkedAuthHandoffToken
      ) {
        throw new Error("No linked account is available yet.");
      }
      await captureOnboardingEmail({
        email: normalizedEmail,
        authMethod: password.trim() ? "password" : "magic_link",
        userMode: submitUserMode,
        surface,
      }).catch(() => null);
      let linkedHandoffToken = "";
      if (!isSignInSurface) {
        const directHandoffToken = String(supportEmailClaimHandoffToken || linkedAuthHandoffToken || "").trim();
        if (directHandoffToken) {
          storeOnboardingHandoffToken(directHandoffToken);
          linkedHandoffToken = directHandoffToken;
        } else {
          const handoff = await createOnboardingHandoff(accountId);
          storeOnboardingHandoffToken(handoff.token);
          linkedHandoffToken = handoff.token;
        }
      }

      if (password.trim()) {
        const { error: signInError } = await client.auth.signInWithPassword({
          email: normalizedEmail,
          password,
        });
        if (signInError) {
          throw signInError;
        }
        let linkedAccountId = "";
        if (!isSignInSurface) {
          linkedAccountId = await persistLinkedAccountLanguages({
            currentAccountId: String(accountId || getCurrentAccountId() || "").trim(),
            handoffToken: linkedHandoffToken || linkedAuthHandoffToken,
            email: normalizedEmail,
            languagePayload,
            adoptOnboardingAccount,
            saveAccountLanguages,
            setCurrentAccountId,
            clearOnboardingHandoffToken,
          });
        }
        persistAuth("verified", { userMode: submitUserMode });
        onAuthenticated?.({
          userMode: "existing",
          accountId: linkedAccountId,
          setupMode: linkedAccountId ? "new" : "",
        });
        return;
      }

      await runRetryableSupabaseAuthOperation(() => client.auth.signInWithOtp({
        email: normalizedEmail,
        options: {
          shouldCreateUser: false,
          emailRedirectTo: getOnboardingEmailRedirectUrl(
            isSignInSurface
              ? signInEmailRedirectPath
              : linkedEmailRedirectPath,
            { handoffToken: linkedHandoffToken },
          ),
        },
      }));

      persistAuth("email-sent", { userMode: submitUserMode });
      setStatus("email-sent");
      setEmailCode("");
    } catch (error) {
      const message = error instanceof Error ? error.message : "We hit a problem. Please try again.";
      setFormError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const completeAuthenticatedFlow = useCallback(async (client) => {
    await syncSupabaseSessionFromUrl({ client });
    const user = await getCurrentSupabaseUser(client);
    const resolvedEmail = String(email || user?.email || user?.new_email || "").trim();
    const resolvedFirstName = String(
      firstName
      || user?.user_metadata?.first_name
      || normalizedStoredAuth.firstName
      || "",
    ).trim();
    const resolvedLastName = String(
      lastName
      || user?.user_metadata?.last_name
      || normalizedStoredAuth.lastName
      || "",
    ).trim();
    const completionHandoffToken = supportEmailClaimHandoffToken || linkedAuthHandoffToken;

    if (userMode === "new") {
      if (!isVerifiedSupabaseEmailUser(user, resolvedEmail)) {
        throw new Error("We can’t see that email verified yet. Open the email link in this browser, then try again.");
      }

      if (isSignInSurface) {
        persistAuth("verified", {
          email: resolvedEmail,
          firstName: resolvedFirstName,
          lastName: resolvedLastName,
        });
        clearOnboardingAuthDraft(authDraftContextKey);
        onAuthenticated?.({ userMode: "new" });
        return;
      }

      if (shouldUpdateVerifiedSignupPassword({ passwordDraft: password, passwordSubmittedDuringSignup: passwordSubmittedDuringSignupRef.current })) {
        const { error: passwordError } = await client.auth.updateUser({
          password,
        });
        if (passwordError) {
          throw passwordError;
        }
      }

      let finalizedAccountId = "";
      try {
        const accountPayload = await getMyAccount();
        finalizedAccountId = String(accountPayload?.account?.id || "").trim();
      } catch (error) {
        const handoffToken = completionHandoffToken;
        if (!handoffToken) {
          throw error;
        }
        const adoptedPayload = await adoptOnboardingAccount(handoffToken, { email: resolvedEmail });
        finalizedAccountId = String(adoptedPayload?.account?.id || "").trim();
      }

      await finalizeOnboardingSignup({
        accountId: finalizedAccountId || getCurrentAccountId(),
        firstName: resolvedFirstName,
        lastName: resolvedLastName,
        email: resolvedEmail,
        ...languagePayload,
      });
      clearOnboardingHandoffToken();
      persistAuth("verified", {
        email: resolvedEmail,
        firstName: resolvedFirstName,
        lastName: resolvedLastName,
      });
      clearOnboardingAuthDraft(authDraftContextKey);
      onAuthenticated?.({ userMode: "new" });
      return;
    }

    if (!isMatchingSupabaseEmailUser(user, resolvedEmail) || user.is_anonymous) {
      throw new Error("Open the sign-in link in this browser, then continue.");
    }

    if (isSignInSurface) {
      persistAuth("verified", { email: resolvedEmail });
      clearOnboardingAuthDraft(authDraftContextKey);
      onAuthenticated?.({ userMode: "existing" });
      return;
    }
    const linkedAccountId = await persistLinkedAccountLanguages({
      currentAccountId: String(getCurrentAccountId() || "").trim(),
      handoffToken: completionHandoffToken,
      email: resolvedEmail,
      languagePayload,
      adoptOnboardingAccount,
      saveAccountLanguages,
      setCurrentAccountId,
      clearOnboardingHandoffToken,
    });
    persistAuth("verified", { email: resolvedEmail });
    clearOnboardingAuthDraft(authDraftContextKey);
    onAuthenticated?.({
      userMode: "existing",
      accountId: linkedAccountId,
      setupMode: "new",
    });
  }, [
    authDraftContextKey,
    email,
    firstName,
    isSignInSurface,
    languagePayload,
    lastName, linkedAuthHandoffToken,
    normalizedStoredAuth.firstName,
    normalizedStoredAuth.lastName,
    onAuthenticated,
    password,
    persistAuth, supportEmailClaimHandoffToken,
    userMode,
  ]);

  useLinkedAuthCallbackRecovery({
    authCallbackErrorHandledRef,
    authDraftContextKey,
    email,
    getCurrentSupabaseUser,
    isSignInSurface,
    languagePayload,
    languageStepComplete,
    normalizedStoredEmail: normalizedStoredAuth.email,
    onAuthenticated,
    setFormError,
    setVerificationError,
    status,
    userMode,
  });

  useEffect(() => {
    if (status !== "email-sent" || autoResumeAttemptedRef.current) {
      return;
    }
    const client = getSupabaseBrowserClient();
    if (!client) {
      return;
    }

    let cancelled = false;

    async function maybeResume() {
      const callbackPresent = typeof window !== "undefined"
        && Boolean(readSupabaseAuthCallbackFromUrl(window.location.href));
      if (!callbackPresent) {
        try {
          const user = await getCurrentSupabaseUser(client);
          if (!isAuthenticatedSupabaseUser(user)) {
            return;
          }
          const matchedEmail = String(user.email || user.new_email || "").trim();
          if (email && matchedEmail && matchedEmail.toLowerCase() !== email.toLowerCase()) {
            return;
          }
        } catch {
          return;
        }
      }

      autoResumeAttemptedRef.current = true;
      if (!cancelled) {
        setVerificationError("");
        setVerifying(true);
      }
      try {
        await completeAuthenticatedFlow(client);
      } catch (error) {
        if (!cancelled) {
          setVerificationError(
            error instanceof Error
              ? error.message
              : "We hit a problem checking that email. Please try again.",
          );
        }
      } finally {
        if (!cancelled) {
          setVerifying(false);
        }
      }
    }

    void maybeResume();

    return () => {
      cancelled = true;
    };
  }, [completeAuthenticatedFlow, email, status]);

  const handleVerified = async () => {
    const client = getSupabaseBrowserClient();
    if (!client) {
      setVerificationError("Supabase is not configured.");
      return;
    }

    setVerificationError("");
    setVerifying(true);

    try {
      await completeAuthenticatedFlow(client);
    } catch (error) {
      setVerificationError(
        error instanceof Error
          ? error.message
          : "We hit a problem checking that email. Please try again.",
      );
    } finally {
      setVerifying(false);
    }
  };

  const handleVerifyCode = async () => {
    const client = getSupabaseBrowserClient();
    if (!client) {
      setVerificationError("Supabase is not configured.");
      return;
    }

    const normalizedCode = normalizeOnboardingEmailCode(emailCode);
    if (normalizedCode.length !== 6) {
      setVerificationError("Enter the 6-digit code from the email.");
      return;
    }

    setVerificationError("");
    setVerifying(true);

    try {
      let lastError = null;
      let verified = false;

      for (const type of getOnboardingEmailCodeVerifyTypes(userMode, {
        linkedContext: !isSignInSurface,
      })) {
        const { error } = await client.auth.verifyOtp({
          email,
          token: normalizedCode,
          type,
        });
        if (!error) {
          verified = true;
          break;
        }
        lastError = error;
      }

      if (!verified) {
        throw lastError || new Error("We couldn’t verify that code. Try the link or request a new email.");
      }

      await completeAuthenticatedFlow(client);
    } catch (error) {
      setVerificationError(
        error instanceof Error
          ? error.message
          : "We hit a problem checking that code. Please try again.",
      );
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="min-h-[100dvh] flex flex-col bg-background">
      <nav className="flex items-center justify-between px-5 py-4">
        <Logo size="sm" />
        <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={onSkip} disabled={isBusy}>
          {isSignInSurface ? t("common.back") : t("common.skip_for_now")}
        </Button>
      </nav>

      <div className="flex-1 flex items-center justify-center px-5 pb-24">
        <motion.div
          className="w-full max-w-md rounded-[2rem] border border-border bg-card/90 p-6 shadow-[0_20px_80px_-40px_rgba(15,23,42,0.5)]"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.22 }}
        >
          {(isSignInSurface || status === "idle") ? (
            <div className={`mb-6 ${isSignInSurface ? "flex items-center gap-3" : "space-y-1"}`}>
              {isSignInSurface ? (
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Mail className="h-7 w-7" />
                </div>
              ) : null}
              <div className="space-y-1">
                <p className="text-sm font-medium text-primary">
                  {isSignInSurface
                    ? tx("Your account")
                    : (!languageStepComplete ? t("auth.language_eyebrow") : t("auth.linked_eyebrow"))}
                </p>
                <h1 className="font-display text-2xl font-bold">
                  {isSignInSurface
                    ? t("landing.sign_in_up")
                    : (!languageStepComplete ? t("auth.language_title") : t("auth.linked_title"))}
                </h1>
                {!isSignInSurface && languageStepComplete ? (
                  <p className="text-sm text-muted-foreground">
                    {tx("Use email to sign in later.")}
                  </p>
                ) : null}
              </div>
            </div>
          ) : null}

          {status === "idle" && (
            !isSignInSurface && !languageStepComplete ? (
              <LinkedAuthLanguageStep
                formError={formError}
                homeLanguageCode={homeLanguageCode}
                isBusy={isBusy}
                languageOptions={languageOptions}
                locale={locale}
                onContinue={() => void handleLanguageStepComplete()}
                onHomeLanguageChange={(nextLanguageCode) => {
                  const normalizedLanguageCode = normalizeLanguageCode(nextLanguageCode);
                  languageTouchedRef.current = true;
                  setHomeLanguageCode(normalizedLanguageCode);
                  setLocale(normalizeUiLanguageCode(normalizedLanguageCode, locale));
                  setUnderstoodLanguageCodes((current) => current.filter((languageCode) => languageCode !== normalizedLanguageCode));
                }}
                onUnderstoodLanguageChange={(languageCode, nextChecked) => {
                  setUnderstoodLanguageCodes((current) => (
                    nextChecked === true
                      ? [...new Set([...current, languageCode])]
                      : current.filter((currentLanguageCode) => currentLanguageCode !== languageCode)
                  ));
                }}
                otherLanguageOptions={otherLanguageOptions}
                submitting={submitting}
                understoodLanguageCodes={understoodLanguageCodes}
              />
            ) : (
            <form className="space-y-4" onSubmit={(event) => void handleSubmit(event)}>
              <div className="space-y-3">
                <Input
                  ref={emailInputRef}
                  autoFocus
                  value={email}
                  disabled={submitting}
                  onBlur={(event) => {
                    const normalizedValue = normalizeOnboardingEmailAddress(event.target.value);
                    if (normalizedValue !== event.target.value) {
                      setEmail(normalizedValue);
                    }
                  }}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="you@company.com"
                  className="h-12 rounded-2xl text-base"
                  {...fieldProps.email}
                />

                {showNameFields && (
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      value={firstName}
                      disabled={submitting}
                      onChange={(event) => setFirstName(event.target.value)}
                      placeholder={tx("First name")}
                      className="h-12 rounded-2xl text-base"
                      {...fieldProps.firstName}
                    />
                    <Input
                      value={lastName}
                      disabled={submitting}
                      onChange={(event) => setLastName(event.target.value)}
                      placeholder={tx("Last name")}
                      className="h-12 rounded-2xl text-base"
                      {...fieldProps.lastName}
                    />
                  </div>
                )}

                {showPasswordField && (
                  <div className="relative">
                    <Input
                      key={fieldProps.password.name} type={showPassword ? "text" : "password"}
                      value={password}
                      disabled={submitting}
                      onChange={(event) => setPassword(event.target.value)}
                      className="h-12 rounded-2xl pr-12 text-base"
                      {...fieldProps.password}
                    />
                    <button
                      type="button"
                      aria-label={showPassword ? tx("Hide password") : tx("Show password")}
                      onClick={() => setShowPassword((current) => !current)}
                      disabled={submitting}
                      className="absolute inset-y-0 right-0 flex w-12 items-center justify-center text-muted-foreground transition-colors hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                )}
              </div>

              {resolvedUserMode === "existing" ? (
                <div className="rounded-2xl bg-muted/70 p-4 text-sm text-muted-foreground">
                  {tx("Leave the password blank and we’ll send a sign-in link instead.")}
                </div>
              ) : null}

              {resolvedUserMode === "new" ? (
                <div className="rounded-2xl bg-muted/70 p-4 text-sm text-muted-foreground">
                  {tx("We’ll verify the email first. If you add a password, we’ll save it after that.")}
                </div>
              ) : null}

              {supportEmailClaimLoading ? (
                <div className="flex items-center gap-2 rounded-2xl bg-muted/70 p-4 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {tx("Opening your email link…")}
                </div>
              ) : null}

              {supportEmailClaim.error ? <p className="text-sm text-destructive">{tx(supportEmailClaim.error)}</p> : null}

              {formError ? <p className="text-sm text-destructive">{tx(formError)}</p> : null}

              <Button
                type="submit"
                size="lg"
                disabled={!canSubmit || isBusy || supportEmailClaim.status === "error"}
                className="h-14 w-full rounded-2xl text-base font-semibold"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {tx(submitPendingLabel)}
                  </>
                ) : (
                  <>
                    {tx(primaryLabel)} <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            </form>
            )
          )}

          {status === "email-sent" && (
            <div className="space-y-4 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Mail className="h-7 w-7" />
              </div>
              <div className="space-y-2">
                <h2 className="font-display text-xl font-bold">{tx("Check your inbox")}</h2>
                <p className="text-sm text-muted-foreground">
                  {userMode === "existing" ? tx("We sent a sign-in link to") : tx("We sent a verification link to")}{" "}
                  {email ? (
                    <span className="font-medium text-foreground">{email}</span>
                  ) : (
                    <span className="font-medium text-foreground">{tx("your email")}</span>
                  )}.
                  {" "}{tx("Enter the 6-digit code below, or use the link in that email instead.")}
                </p>
              </div>
              <div className="space-y-3">
                {verificationError && (
                  <p className="text-sm text-destructive">{tx(verificationError)}</p>
                )}
                <form
                  className="space-y-3 rounded-2xl border border-border/70 bg-muted/40 p-4 text-left"
                  onSubmit={(event) => {
                    event.preventDefault();
                    void handleVerifyCode();
                  }}
                >
                  <label
                    htmlFor={fieldProps.emailCode.id}
                    className="block text-sm font-medium text-foreground"
                  >
                    {userMode === "existing" ? tx("Sign-in code") : tx("Verification code")}
                  </label>
                  <Input
                    value={emailCode}
                    disabled={verifying}
                    onChange={(event) => setEmailCode(normalizeOnboardingEmailCode(event.target.value))}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        void handleVerifyCode();
                      }
                    }}
                    className="h-12 rounded-2xl text-base tracking-[0.24em]"
                    maxLength={6}
                    placeholder={tx("6-digit code")}
                    {...fieldProps.emailCode}
                  />
                  <Button
                    type="submit"
                    className="h-12 w-full rounded-2xl text-base font-semibold"
                    disabled={verifying}
                  >
                    {verifying ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {tx("Verifying…")}
                      </>
                    ) : (
                      tx("Continue with code")
                    )}
                  </Button>
                </form>
                <Button
                  variant="ghost"
                  className="w-full text-muted-foreground"
                  onClick={() => void handleVerified()}
                  disabled={verifying}
                >
                  {userMode === "existing" ? tx("I used the sign-in link") : tx("I used the email link")}
                </Button>
                <Button
                  variant="ghost"
                  className="w-full text-muted-foreground"
                  onClick={handleUseAnotherEmail}
                  disabled={verifying}
                >
                  {tx("Use another email")}
                </Button>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
